import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Autocomplete from 'react-autocomplete'
import { Button, Image, InputGroup } from 'react-bootstrap'

import SearchForm from '../../../components/Subheader/Form'
import { API_OPERATIONS } from '../constants'
import { TrackingHOC } from '../../../hocs'
import debounce from 'lodash/debounce'

import { useKendra } from '../../../hooks'
import searchImage from '../images/search-image.svg'
import { browserHistory } from 'react-router'

const SUGGESTIONS_KEYSTROKE_DELAY = 200 // adjust as needed
const MIN_TEXT_LENGTH_FOR_SUGGESTIONS = 2
const MAX_TEXT_LENGTH_FOR_SUGGESTIONS = 60

/**
 * Builds a suggestion item with highlighted label.
 *
 * @param {Object} suggestion - The suggestion object.
 * @returns {Object} The suggestion item with highlighted label.
 */
const buildSuggestionItemWithHighlightedLabel = suggestion => {
  if (suggestion && suggestion.Value && suggestion.Value.Text) {
    let suggestionText = suggestion.Value.Text.Text
    const suggestionHighlights = suggestion.Value.Text.Highlights
    if (suggestionText && suggestionHighlights) {
      const suggestionHighlight = suggestionHighlights[0]
      if (suggestionHighlight && suggestionHighlight.BeginOffset) {
        // split text at BeginOffset and bold second part
        let label = (
          <span>
            {suggestionText.substring(0, suggestionHighlight.BeginOffset)}
            <strong>
              {suggestionText.substring(suggestionHighlight.BeginOffset)}
            </strong>
          </span>
        )
        return { label, key: suggestionText }
      }
    }
    // set suggestionText as label if no highlights
    return { label: suggestionText, key: suggestionText }
  }
}

/**
 * Builds suggestion items from the suggestions array.
 *
 * @param {Array} suggestions - The array of suggestions.
 * @returns {Array} The array of suggestion items.
 */
const buildSuggestionItems = suggestions => {
  if (!suggestions || suggestions.length === 0) {
    return []
  }
  return suggestions.map(suggestion =>
    buildSuggestionItemWithHighlightedLabel(suggestion)
  )
}

const inputStyles = {
  display: 'block',
  width: '100%',
  padding: '6px 12px',
  lineHeight: '1.42857143',
  color: '#555',
  border: 0,
  borderRadius: '4px',
  boxShadow: 'inset 0 1px 1px rgba(0,0,0,.075)',
  transition: 'border-color ease-in-out .15s,box-shadow ease-in-out .15s'
}

const menuStyles = {
  borderRadius: '3px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
  background: 'rgba(255, 255, 255, 0.9)',
  padding: '0px 12px',
  fontSize: '90%',
  position: 'fixed',
  overflow: 'hidden',
  maxHeight: '50%',
  zIndex: 10
}

/**
 * SearchBar component for displaying a search input with suggestions.
 * Uses the useKendra hook for search methods sans-props.
 *
 * @see {@link https://github.com/aws-samples/aws-kendra-transcribe-media-search/blob/main/finderapp/src/search/searchBar/SearchBar.tsx SearchBar.tsx - AWS Samples}
 * @example
 * return <SearchBar />
 *
 * @param {Object} props - The component props (unused).
 * @returns {JSX.Element} The rendered SearchBar component.
 */
const SearchBar = props => {
  const {
    setSearch,
    getResults,
    search,
    kendra,
    pushEvent
  } = useKendra()
  const [queryText, setQueryText] = useState('')

  /**
   * Fetches query suggestions from the API.
   *
   * @param {string} _indexId - The index ID.
   * @param {string} _queryText - The query text.
   * @returns {Promise<Array|null>} The array of suggestions or null if an error occurs.
   */
  const getQuerySuggestions = async (_indexId, _queryText) => {
    try {
      const response = await kendra(API_OPERATIONS.GET_QUERY_SUGGESTIONS, {
        IndexId: _indexId,
        QueryText: _queryText
      })
      if (response) {
        return response.Suggestions
      }
    } catch (e) {
      setSearch({ type: 'ERROR', payload: e })
      console.log('Error in query suggestions config')
    }
    return null
  }

  /**
   * Fetches suggestions based on the input value.
   *
   * @param {string} value - The input value.
   * @returns {Promise<Array|null>} The array of suggestions or null if an error occurs.
   */
  const getSuggestions = async value => {
    const result = await getQuerySuggestions(search.indexId, value)
    if (result) {
      setSearch({ type: 'SUGGESTIONS', payload: result })
    }
    return result
  }

  /**
   * Populates suggestions with a debounce delay.
   */
  const populateSuggestions = useCallback(
    debounce(getSuggestions, SUGGESTIONS_KEYSTROKE_DELAY)
  )

  /**
   * Handles the change event of the input field.
   *
   * @param {Object} event - The change event.
   */
  const onChange = async event => {
    const { value } = event.target
    setQueryText(value)

    if (
      search.suggestionsEnabled &&
      value.length >= MIN_TEXT_LENGTH_FOR_SUGGESTIONS &&
      value.length <= MAX_TEXT_LENGTH_FOR_SUGGESTIONS
    ) {
      await populateSuggestions(value)
    } else {
      setSearch({ type: 'SUGGESTIONS', payload: [] })
    }
  }

  /**
   * Handles the search event.
   *
   * @param {Object} event - The search event.
   */
  const onSearch = event => {
    event.preventDefault()
    event.stopPropagation()
    setSearch({ type: 'QUERY_TEXT', payload: queryText })
    setSearch({ type: 'CURRENT_PAGE_NUMBER', payload: 1 })
    pushEvent({
      action: 'submit',
      search_term: queryText
    })
    return getResults(queryText, 1)
  }

  /**
   * Handles the selection of a suggestion.
   *
   * @param {string} value - The selected suggestion value.
   */
  const onSelect = value => {
    setQueryText(value)
    pushEvent({
      action: 'select_suggestion',
      search_term: value
    })
    return getResults(value, 1)
  }

  const suggestionItems = useMemo(() => {
    return buildSuggestionItems(search.suggestions)
  }, [search.suggestions])


  useEffect(() => {
    const unlisten = browserHistory.listen(() => {
      setQueryText('')
    })
    return () => {
      unlisten()
    }
  }, [])

  return (
    <SearchForm
      style={{
        background: 'var(--main-color)',
        width: '80%',
        margin: '0px auto',
        borderRadius: 6
      }}
      onSubmit={onSearch}
    >
      <InputGroup
        style={{
          width: '100%',
          background: 'rgba(255, 255, 255, 0.9)',
          border: '1px solid #ccc',
          borderRadius: 6
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            background: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid #ccc',
            borderRadius: 6
          }}
        >
          <Autocomplete
            getItemValue={item => item.key}
            items={suggestionItems}
            renderItem={(item, isHighlighted) => (
              <div
                style={{
                  background: isHighlighted ? '#ddfaff' : 'white',
                  cursor: 'pointer'
                }}
                key={item.key}
              >
                {item.label}
              </div>
            )}
            value={queryText}
            onChange={onChange}
            onSelect={onSelect}
            autoHighlight={false}
            inputProps={{
              style: inputStyles,
              placeholder: 'How can we help you?'
            }}
            wrapperStyle={{
              width: '100%',
              position: 'relative'
            }}
            menuStyle={menuStyles}
          />
          <Button
            variant="outline-secondary"
            style={{
              border: 0
            }}
            onClick={onSearch}
          >
            <Image src={searchImage} rounded />
          </Button>
        </div>
      </InputGroup>
    </SearchForm>
  )
}

const WrappedSearchBar = TrackingHOC(SearchBar)
export default WrappedSearchBar
